import { trigger } from '@angular/animations';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  model,
  output,
  signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { GalleryDto, WatermarkDto } from '@nusign/picflow-backend-types';
import { maxHeightIf } from '../../../animations/max-height.if.animation';
import { SwitchComponent } from '../../../components/switch/switch.component';
import { GalleryService } from '../../../services/gallery.service';
import { WatermarkService } from '../../../services/watermark.service';
import { UpgradeBadgeComponent } from '../../../shared-extra/components/pro-tag/upgrade-badge.component';
import { BillingScopePipe } from '../../../shared-extra/pipes/billing-scope.pipe';
import { ContextMenuComponent } from '../../../shared/components/context-menu/context-menu.component';
import { SvgComponent } from '../../../shared/components/svg/svg.component';
import { ButtonDirective } from '../../../shared/directives/button.directive';
import { WatermarkUrlPipe } from '../../../shared/pipes/watermark-url.pipe';
import { AccessItemCtaComponent } from '../access-item-cta/access-item-cta.component';
import { AccessItemHeaderComponent } from '../access-item-header/access-item-header.component';
import { AccessItemIconComponent } from '../access-item-icon/access-item-icon.component';
import { AccessItemComponent } from '../access-item/access-item.component';
import { GallerySettingsIconComponent } from '../gallery-settings-icon/gallery-settings-icon.component';

@Component({
  selector: 'app-gallery-watermark-setting-item',
  imports: [
    AccessItemComponent,
    AccessItemHeaderComponent,
    AccessItemCtaComponent,
    AccessItemIconComponent,
    SwitchComponent,
    FormsModule,
    GallerySettingsIconComponent,
    UpgradeBadgeComponent,
    BillingScopePipe,
    ButtonDirective,
    SvgComponent,
    RouterLink,
    ContextMenuComponent,
    WatermarkUrlPipe,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [trigger('watermarkSettings', maxHeightIf(32))],
  templateUrl: './gallery-watermark-setting-item.component.html',
  styleUrls: ['./gallery-watermark-setting-item.component.scss'],
})
export class GalleryWatermarkSettingItemComponent {
  private galleryService = inject(GalleryService);
  private watermarkService = inject(WatermarkService);
  gallery = input.required<GalleryDto>();
  galleryWatermark = model<boolean>();
  customize = output<void>();
  watermarks = toSignal(this.watermarkService.watermarks$);
  selectedWatermark = signal<WatermarkDto>(undefined);
  keepGalleryWatermarkEnabled = false;

  currentWatermark = computed(() => {
    const gallery = this.gallery();
    const watermarks = this.watermarks();
    if (!gallery || !watermarks) return;
    if (!gallery.watermark) return;
    return watermarks.find(w => w.id === gallery.watermark);
  });

  constructor() {
    effect(() => {
      const gallery = this.gallery();
      const currentWatermark = this.currentWatermark();
      if (!gallery) return;
      if (!this.keepGalleryWatermarkEnabled) {
        this.galleryWatermark.set(!!gallery.watermark);
      }
      if (currentWatermark) {
        this.selectedWatermark.set(currentWatermark);
      }
    });
  }

  updateGalleryWatermark() {
    this.keepGalleryWatermarkEnabled = this.galleryWatermark();
    if (!this.galleryWatermark()) {
      this.galleryService.updateGallery(this.gallery(), {
        watermark: null,
      });
    }
  }

  selectWatermark(watermark: WatermarkDto) {
    this.selectedWatermark.set(watermark);
  }

  applyWatermark() {
    this.galleryService.updateGallery(this.gallery(), {
      watermark: this.selectedWatermark().id,
    });
  }
}
